/**
 * Column-major 4x4 matrices, the layout WGSL expects for mat4x4<f32>.
 *
 * Just the handful of operations the room view needs. Depth range is 0..1
 * rather than OpenGL's -1..1, because that is WebGPU's clip convention.
 */

export type Mat4 = Float32Array;
export type Vec3 = [number, number, number];

export function identity(): Mat4 {
  const m = new Float32Array(16);
  m[0] = m[5] = m[10] = m[15] = 1;
  return m;
}

/** Right-handed perspective projection with a 0..1 depth range. */
export function perspective(fovYRadians: number, aspect: number, near: number, far: number): Mat4 {
  const f = 1 / Math.tan(fovYRadians / 2);
  const m = new Float32Array(16);
  m[0] = f / aspect;
  m[5] = f;
  m[10] = far / (near - far);
  m[11] = -1;
  m[14] = (far * near) / (near - far);
  return m;
}

/** Right-handed look-at view matrix. */
export function lookAt(eye: Vec3, target: Vec3, up: Vec3): Mat4 {
  const z = normalize(subtract(eye, target));
  const x = normalize(cross(up, z));
  const y = cross(z, x);
  const m = new Float32Array(16);
  m[0] = x[0]; m[1] = y[0]; m[2] = z[0]; m[3] = 0;
  m[4] = x[1]; m[5] = y[1]; m[6] = z[1]; m[7] = 0;
  m[8] = x[2]; m[9] = y[2]; m[10] = z[2]; m[11] = 0;
  m[12] = -dot(x, eye);
  m[13] = -dot(y, eye);
  m[14] = -dot(z, eye);
  m[15] = 1;
  return m;
}

export function multiply(a: Mat4, b: Mat4): Mat4 {
  const out = new Float32Array(16);
  for (let column = 0; column < 4; column++) {
    for (let row = 0; row < 4; row++) {
      let sum = 0;
      for (let k = 0; k < 4; k++) sum += a[k * 4 + row] * b[column * 4 + k];
      out[column * 4 + row] = sum;
    }
  }
  return out;
}

/** Project a world point to normalised device coordinates; w < 0 means behind the camera. */
export function project(matrix: Mat4, point: Vec3): { x: number; y: number; w: number } {
  const [px, py, pz] = point;
  const x = matrix[0] * px + matrix[4] * py + matrix[8] * pz + matrix[12];
  const y = matrix[1] * px + matrix[5] * py + matrix[9] * pz + matrix[13];
  const w = matrix[3] * px + matrix[7] * py + matrix[11] * pz + matrix[15];
  if (Math.abs(w) < 1e-6) return { x: 0, y: 0, w: 0 };
  return { x: x / w, y: y / w, w };
}

export function subtract(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

export function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

export function cross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

export function normalize(v: Vec3): Vec3 {
  const length = Math.hypot(v[0], v[1], v[2]);
  return length < 1e-9 ? [0, 0, 0] : [v[0] / length, v[1] / length, v[2] / length];
}
